import { useTranslation } from "react-i18next";
import { Activity } from "lucide-react";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { ApiError } from "@/lib/api/client";
import { formatRelativeTime } from "@/lib/formatters";
import type { DeploymentMetrics, Package } from "../api/deployments.api";
import {
  distributionRows,
  failureRate,
  finiteCount,
  formatFailureRatePercent,
  relatedReportingLabels,
  totalActive,
} from "../lib/metrics";

interface DeploymentMetricsPanelProps {
  metrics: DeploymentMetrics | undefined;
  heldSince: number | undefined;
  isPending: boolean;
  isSuccess: boolean;
  historyReady: boolean;
  error: ApiError | null | undefined;
  currentRelease: Package | undefined;
  history: Package[];
}

function formatCount(value: number | null | undefined): string {
  const n = finiteCount(value);
  return n === null ? "—" : n.toLocaleString();
}

export function DeploymentMetricsPanel({
  metrics,
  heldSince,
  isPending,
  isSuccess,
  historyReady,
  error,
  currentRelease,
  history,
}: DeploymentMetricsPanelProps) {
  const { t } = useTranslation();
  const map: DeploymentMetrics = metrics ?? {};
  const hasMap = Object.keys(map).length > 0;
  const isEmpty = isSuccess && !isPending && !error && !hasMap;
  const currentLabel = currentRelease?.label;
  const currentMetrics = currentLabel ? map[currentLabel] : undefined;
  const related = relatedReportingLabels(currentRelease, history);
  const relatedWithCounts = related.filter((label) => finiteCount(map[label]?.active) !== null);
  const newestFirstLabels = [...history].reverse().flatMap((pkg) => (pkg.label ? [pkg.label] : []));
  const rows = hasMap ? distributionRows(map, newestFirstLabels, historyReady) : [];
  const actives = totalActive(map);
  const rate = formatFailureRatePercent(failureRate(currentMetrics));

  return (
    <section className="mt-8 rounded-xl border border-border bg-surface p-5">
      <div className="flex items-center gap-2">
        <Activity size={16} className="text-muted" />
        <h2 className="text-lg font-semibold text-heading">{t("deployments.metricsTitle")}</h2>
      </div>
      <p className="mt-1 text-xs text-muted">{t("deployments.metricsSignal")}</p>

      <ApiErrorBanner error={error} className="mt-4" />

      {isPending && !error && !hasMap ? (
        <p className="mt-4 text-sm text-muted">{t("common.loading")}</p>
      ) : null}

      {isEmpty ? <p className="mt-4 text-sm text-muted">{t("deployments.metricsEmpty")}</p> : null}

      {heldSince !== undefined && isSuccess ? (
        <p className="mt-4 text-xs text-muted">
          {t("deployments.metricsHeld", { time: formatRelativeTime(heldSince) })}
        </p>
      ) : null}

      {hasMap ? (
        <div className="mt-4 space-y-4">
          {currentRelease ? (
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-muted">
                  {t("deployments.latestRelease")}
                </span>
                {currentLabel ? (
                  <span className="font-mono text-sm font-semibold text-heading">
                    {currentLabel}
                  </span>
                ) : null}
                {currentRelease.uploadTime ? (
                  <span className="text-xs text-muted">
                    {formatRelativeTime(currentRelease.uploadTime)}
                  </span>
                ) : null}
                {currentRelease.isMandatory ? (
                  <span className="rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
                    {t("deployments.mandatory")}
                  </span>
                ) : null}
                {currentRelease.isDisabled ? (
                  <span className="rounded-full bg-danger/10 px-2 py-0.5 text-xs font-medium text-danger">
                    {t("deployments.disabled")}
                  </span>
                ) : null}
                {currentRelease.rollout != null && currentRelease.rollout !== 100 ? (
                  <span className="rounded-full bg-elevated px-2 py-0.5 text-xs font-medium text-muted">
                    {t("deployments.rolloutBadge", { percent: currentRelease.rollout })}
                  </span>
                ) : null}
              </div>
              {currentRelease.rollout != null && currentRelease.rollout !== 100 ? (
                <p className="mt-2 text-xs text-muted">{t("deployments.metricsPartialRollout")}</p>
              ) : null}
              <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
                <MetricStat
                  label={t("deployments.metricsActive")}
                  value={formatCount(currentMetrics?.active)}
                />
                <MetricStat
                  label={t("deployments.metricsDownloaded")}
                  value={formatCount(currentMetrics?.downloaded)}
                />
                <MetricStat
                  label={t("deployments.metricsInstalled")}
                  value={formatCount(currentMetrics?.installed)}
                />
                <MetricStat
                  label={t("deployments.metricsFailed")}
                  value={formatCount(currentMetrics?.failed)}
                />
                <MetricStat label={t("deployments.metricsFailureRate")} value={rate ?? "—"} />
              </dl>
              <p className="mt-2 text-xs text-muted">{t("deployments.metricsDeviceCounts")}</p>
            </div>
          ) : null}

          {relatedWithCounts.map((label) => (
            <p key={label} className="text-xs text-muted">
              {t("deployments.metricsRelatedHash", {
                label,
                active: formatCount(map[label]?.active),
              })}
            </p>
          ))}

          {rows.length > 0 ? (
            <div>
              <h3 className="text-xs font-medium uppercase tracking-wide text-muted">
                {t("deployments.metricsDistribution")}
              </h3>
              <ul className="mt-2 divide-y divide-border">
                {rows.map((row) => {
                  const key = row.kind === "release" ? row.label : row.key;
                  const active = finiteCount(row.metrics.active) ?? 0;
                  const share = actives > 0 ? Math.round((active / actives) * 100) : 0;
                  return (
                    <li
                      key={`${row.kind}:${key}`}
                      className="flex items-center justify-between gap-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="font-mono text-sm text-heading">{key}</p>
                        {row.kind === "binary" ? (
                          <p className="text-xs text-muted">{t("deployments.metricsBinary")}</p>
                        ) : null}
                        {row.kind === "unknown" ? (
                          <p className="text-xs text-muted">
                            {t("deployments.metricsUnknownLabel")}
                          </p>
                        ) : null}
                      </div>
                      <p className="shrink-0 text-xs text-muted">
                        {formatCount(row.metrics.active)}
                        {actives > 0 ? ` · ${share}%` : ""}
                      </p>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function MetricStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-elevated/50 px-3 py-2">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="mt-0.5 text-sm font-semibold text-heading">{value}</dd>
    </div>
  );
}
